import { api } from '../api.js';
import { createPaginationNavigationGuard } from '../pagination-navigation.js';
import { navigate } from '../router.js';
import { esc } from '../../../../src/shared/escape-html.js';

function relTime(iso) {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  if (ms < 3600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86400_000) return `${Math.floor(ms / 3600_000)}h ago`;
  return `${Math.floor(ms / 86400_000)}d ago`;
}

function fullTime(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    + ' ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

let _bound = false;
let _page = 1;
let _cursor = null;
let _nextCursor = null;
let _cursorHistory = [];
let _loadGen = 0;
const _navigation = createPaginationNavigationGuard();
const _limit = 50;

function resetPagination() {
  _navigation.reset();
  _page = 1;
  _cursor = null;
  _nextCursor = null;
  _cursorHistory = [];
}

function getFilters() {
  return {
    q: document.getElementById('ce-search')?.value?.trim() || '',
    system: document.getElementById('ce-system')?.value || '',
    range: document.getElementById('ce-range')?.value || '',
  };
}

async function loadList() {
  const gen = ++_loadGen;
  const body = document.getElementById('ce-body');
  const status = document.getElementById('ce-status');
  const pageInfo = document.getElementById('ce-page-info');
  if (!body) return;

  status.textContent = 'Loading...';
  body.innerHTML = '';

  const f = getFilters();
  const params = new URLSearchParams();
  params.set('limit', _limit);
  if (_cursor) params.set('cursor', _cursor);
  if (f.q) params.set('q', f.q);
  if (f.system) params.set('system', f.system);
  if (f.range) params.set('range', f.range);

  try {
    const data = await api.get(`/api/admin/client-errors?${params}`);
    if (gen !== _loadGen) return 'stale';
    _nextCursor = data.next_cursor;
    status.textContent = '';
    pageInfo.textContent = `Page ${_page}`;
    document.getElementById('ce-prev').disabled = _cursorHistory.length === 0;
    document.getElementById('ce-next').disabled = !data.has_more;

    if (!data.errors.length) {
      status.textContent = 'No errors found.';
      return true;
    }

    body.innerHTML = data.errors.map(e => {
      const msg = (e.error_preview || '').slice(0, 80) + (e.error_preview?.length > 80 ? '...' : '');
      return `
      <tr class="contact-row-clickable" data-id="${esc(e.id)}">
        <td style="white-space:nowrap" title="${esc(fullTime(e.created_at))}">${esc(relTime(e.created_at))}</td>
        <td>${esc(e.system_id || '–')}</td>
        <td title="${esc(e.rom_name || '')}">${esc((e.rom_name || '–').slice(0, 30))}</td>
        <td class="contact-excerpt" title="${esc(e.error_preview || '')}">${esc(msg)}</td>
        <td style="white-space:nowrap">${esc(e.device_class || '–')}</td>
        <td>${esc(e.username || '–')}</td>
      </tr>`;
    }).join('');

    body.querySelectorAll('.contact-row-clickable').forEach(tr => {
      tr.addEventListener('click', () => {
        navigate(`/admin/client-errors/${tr.dataset.id}`);
      });
    });
    return true;
  } catch {
    if (gen !== _loadGen) return 'stale';
    status.textContent = 'Failed to load errors.';
    return false;
  }
}

let _searchTimer = null;

function bindFilters() {
  if (_bound) return;
  _bound = true;

  document.getElementById('ce-search')?.addEventListener('input', () => {
    clearTimeout(_searchTimer);
    _searchTimer = setTimeout(() => { resetPagination(); loadList(); }, 300);
  });

  document.getElementById('ce-system')?.addEventListener('change', () => { resetPagination(); loadList(); });
  document.getElementById('ce-range')?.addEventListener('change', () => { resetPagination(); loadList(); });

  document.getElementById('ce-prev')?.addEventListener('click', async () => {
    if (!_cursorHistory.length) return;
    await _navigation.run(async () => {
      const snap = { cursor: _cursor, page: _page, history: [..._cursorHistory] };
      _cursor = _cursorHistory.pop() || null;
      _page--;
      if (!await loadList()) {
        _cursor = snap.cursor; _page = snap.page; _cursorHistory = snap.history;
      }
    });
  });
  document.getElementById('ce-next')?.addEventListener('click', async () => {
    if (!_nextCursor) return;
    await _navigation.run(async () => {
      const snap = { cursor: _cursor, page: _page, history: [..._cursorHistory] };
      _cursorHistory.push(_cursor);
      _cursor = _nextCursor;
      _page++;
      if (!await loadList()) {
        _cursor = snap.cursor; _page = snap.page; _cursorHistory = snap.history;
      }
    });
  });
}

function kvRows(obj) {
  if (!obj || typeof obj !== 'object') return '<div style="color:var(--text-dim)">–</div>';
  const parsed = typeof obj === 'string' ? JSON.parse(obj) : obj;
  const entries = Object.entries(parsed);
  if (!entries.length) return '<div style="color:var(--text-dim)">Empty</div>';
  return entries.map(([k, v]) => {
    const display = v === null || v === undefined ? '–' : typeof v === 'object' ? JSON.stringify(v) : String(v);
    return `<div class="contact-detail-meta-row"><span class="contact-detail-meta-label">${esc(k)}</span><span style="word-break:break-all">${esc(display)}</span></div>`;
  }).join('');
}

async function loadDetail(id) {
  const content = document.getElementById('ce-detail-content');
  if (!content) return;
  content.innerHTML = '<div class="admin-status">Loading...</div>';

  try {
    const e = await api.get(`/api/admin/client-errors/${id}`);
    const di = typeof e.device_info === 'string' ? JSON.parse(e.device_info) : (e.device_info || {});
    const wi = typeof e.wasm_info === 'string' ? JSON.parse(e.wasm_info) : (e.wasm_info || {});

    content.innerHTML = `
      <div class="card admin-card">
        <div class="card-label">ERROR</div>
        <div class="contact-detail-meta">
          <div class="contact-detail-meta-row"><span class="contact-detail-meta-label">Name</span><span>${esc(e.error_name || '–')}</span></div>
          <div class="contact-detail-meta-row"><span class="contact-detail-meta-label">Message</span><span>${esc(e.error_message)}</span></div>
          <div class="contact-detail-meta-row"><span class="contact-detail-meta-label">Time</span><span>${esc(fullTime(e.created_at))}</span></div>
          <div class="contact-detail-meta-row"><span class="contact-detail-meta-label">User</span><span>${e.username ? `<a href="/u/${esc(e.username)}" style="color:var(--accent)">${esc(e.username)}</a>` : 'Anonymous'}</span></div>
        </div>
      </div>

      <div class="card admin-card">
        <div class="card-label">CONTEXT</div>
        <div class="contact-detail-meta">
          <div class="contact-detail-meta-row"><span class="contact-detail-meta-label">System</span><span>${esc(e.system_id || '–')}</span></div>
          <div class="contact-detail-meta-row"><span class="contact-detail-meta-label">ROM</span><span>${esc(e.rom_name || '–')}</span></div>
          <div class="contact-detail-meta-row"><span class="contact-detail-meta-label">ROM Hash</span><span style="font-family:var(--mono)">${esc(e.rom_hash || '–')}</span></div>
          <div class="contact-detail-meta-row"><span class="contact-detail-meta-label">App State</span><span>${esc(e.app_state || '–')}</span></div>
          <div class="contact-detail-meta-row"><span class="contact-detail-meta-label">Frame</span><span>${e.frame_count ?? '–'}</span></div>
          <div class="contact-detail-meta-row"><span class="contact-detail-meta-label">Uptime</span><span>${e.uptime_ms != null ? `${Math.round(e.uptime_ms / 1000)}s` : '–'}</span></div>
          <div class="contact-detail-meta-row"><span class="contact-detail-meta-label">Audio</span><span>${esc(e.audio_state || '–')}</span></div>
          <div class="contact-detail-meta-row"><span class="contact-detail-meta-label">WebGL Lost</span><span>${e.webgl_lost === true ? 'Yes' : e.webgl_lost === false ? 'No' : '–'}</span></div>
        </div>
      </div>

      ${e.error_stack ? `
      <div class="card admin-card">
        <div class="card-label">STACK TRACE</div>
        <pre class="ce-stack">${esc(e.error_stack)}</pre>
      </div>` : ''}

      <div class="card admin-card">
        <div class="card-label">DEVICE INFO</div>
        <div class="contact-detail-meta">${kvRows(di)}</div>
      </div>

      <div class="card admin-card">
        <div class="card-label">WASM INFO</div>
        <div class="contact-detail-meta">${kvRows(wi)}</div>
      </div>

      <div class="card admin-card">
        <div class="card-label">RAW</div>
        <div class="contact-detail-meta">
          <div class="contact-detail-meta-row"><span class="contact-detail-meta-label">IP</span><span style="font-family:var(--mono)">${esc(e.ip || '–')}</span></div>
          <div class="contact-detail-meta-row"><span class="contact-detail-meta-label">User Agent</span><span style="font-size:0.75em;word-break:break-all">${esc(e.user_agent || '–')}</span></div>
          <div class="contact-detail-meta-row"><span class="contact-detail-meta-label">ID</span><span style="font-family:var(--mono);font-size:0.75em">${esc(e.id)}</span></div>
        </div>
      </div>
    `;
  } catch {
    content.innerHTML = '<div class="admin-status">Failed to load error detail.</div>';
  }
}

export function renderList() {
  bindFilters();
  resetPagination();
  loadList();
}

export function renderDetail(params) {
  loadDetail(params.id);
}
